import { Component, OnInit } from '@angular/core';
import { ModalController, NavController, Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Logger } from 'ethers/lib/utils';
import { take } from 'rxjs/operators';
import { ChangeActivePage } from 'src/app/angular-wallet-base/actions/appSettings.actions';
import { WALLET_STORAGE_KEY } from 'src/app/angular-wallet-base/constants';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { AuthService } from 'src/app/angular-wallet-base/services/auth.service';
import { EnvironmentService } from 'src/app/angular-wallet-base/services/environment.service';
import { MembersPortalService } from 'src/app/angular-wallet-base/services/members-portal.service';
import { StorageService } from 'src/app/angular-wallet-base/services/storage.service';
import { getActivePage, getSideMenuStatus } from 'src/app/angular-wallet-base/store/appSettings';
import { AppState } from 'src/app/angular-wallet-base/store/appState';
import { SubscriptionListenerComponent } from 'src/app/angular-wallet-base/SubscriptionListenerComponent';
import { openKycModal } from 'src/app/angular-wallet-base/utils';
import { SelectAssetModal } from 'src/app/views/select-asset-modal/select-asset-modal.component';
import { SelectWalletModalComponent } from 'src/app/views/select-asset-modal/select-wallet-modal/select-wallet-modal.component';
import { environment } from 'src/environments/environment';
import { ComplianceService } from '../../../angular-wallet-base/services/compliance.service';
import { RoutingService } from '../../../angular-wallet-base/services/routing.service';
import { BeforeActivationComponent } from '../../before-activation/before-activation.component';
import { OnRamperComponent } from '../../on-ramper/on-ramper.component';

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
})
export class TabsComponent extends SubscriptionListenerComponent implements OnInit {

  public activeTab;
  public vaultStatus = false;
  public isDesktopMode;
  public totalAssetBalance = 0;
  public tabs = [
    {
      id: 'home',
      title: 'tabs.home',
      icon: 'home',
      disabled: false,
      hidden: false
    },
    {
      id: 'send',
      title: 'tabs.send',
      icon: 'send',
      disabled: this.totalAssetBalance === 0 && this.vaultStatus,
      hidden: false
    },
    {
      id: 'receive',
      title: 'tabs.receive',
      icon: 'download',
      disabled: false,
      hidden: false
    },
    {
      id: 'buy',
      title: 'tabs.buy',
      icon: 'cart',
      disabled: false,
      hidden: false
    },
    {
      id: 'trade',
      title: 'tabs.swap',
      icon: 'swap-horizontal',
    }
  ];
  constructor(
    protected store: Store<AppState>,
    private storage: StorageService,
    private modalCtrl: ModalController,
    private appMode: AppModeService,
    private nav: NavController,
    private membersPortalService: MembersPortalService,
    private routingService: RoutingService,
    private authService:AuthService,
    private complianceService: ComplianceService,
    private environmentService: EnvironmentService,
    private platform: Platform
  ) {
    super(store);
    this.subscriptions.add(this.store.select(getActivePage).subscribe(activePage => {
      const currentTab = this.tabs.some(tab => tab.id === activePage);
      this.activeTab = currentTab ? activePage : 'home';
    }));
    this.appMode.isDesktopMode.subscribe(mode => {
      this.isDesktopMode = mode;
      if (this.isDesktopMode) {
        this.store.dispatch(new ChangeActivePage('home'));
        this.dismissModal();
      }
    });

    this.storage.totalBalance.subscribe(balance => {
      this.totalAssetBalance = balance;
    });
  }

  async ngOnInit() {
    this.vaultStatus = (await this.storage.get(WALLET_STORAGE_KEY)) ? true : false;
  }

  async navigateTo(page) {
    if (!this.vaultStatus && !this.isDesktopMode) {
      if (page.id !== 'home') {
        await this.dismissModal();
        this.store.dispatch(new ChangeActivePage(page.id));
        const modal = await this.modalCtrl.create({
          component: BeforeActivationComponent,
          mode: 'ios',
          cssClass: 'before-activation',
          initialBreakpoint: 0.7,
          breakpoints: [0, 0.2, 0.4, 0.6, 0.7, 0.8, 1],
          backdropDismiss: true,
          componentProps: { currentPage: page.id, type: 'modal' }
        });
        await modal.present();
        await modal.addEventListener('ionBreakpointDidChange', event => {
          if (event && event['detail'].breakpoint <= 0.2) {
            modal.dismiss();
          }
        });

        const { data } = await modal.onWillDismiss();
        if (data !== undefined) {
          if (data.link) {
            this.nav.navigateForward(data.link);
          }
        } else {
          if (page.id !== 'home') {
            this.store.dispatch(new ChangeActivePage('home'));
          }
        }
      } else {
        this.store.dispatch(new ChangeActivePage('home'));
        await this.dismissModal();
      }
    } else {
      switch (page.id) {
        case 'send':
          this.presentModal('send');
          break;
        case 'receive':
          this.presentModal('receive');
          break;
        case 'buy':
          this.presentModal('buy');
          // this.openSelectWalletModal('buy');
          break;
        case 'trade':
          await this.nav.navigateForward('trade');
          break;
        case 'home':
          await this.routingService.goHome();
          break;
      }
    }
  }

  async openSelectWalletModal(mode) {
    const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();
    let cssClass = "";
    cssClass += this.isDesktopMode ? "select-asset-modal-desktop" : "select-asset-modal";
    cssClass += !sideMenuHidden && this.isDesktopMode ? " with-side-menu" : "";
    const modal = await this.modalCtrl.create({
      component: SelectWalletModalComponent,
      componentProps: {
      },
      cssClass,
      initialBreakpoint: this.isDesktopMode ? undefined : 0.5,
      breakpoints: this.isDesktopMode ? undefined : [0, 0.5, 0.6, 0.9, 1],
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.type) {
      this.presentModal(data.type);
    }
  }

  async presentModal(mode: string) {
    const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();

    if (mode == 'on-ramper') {
      const sideMenuHidden = await this.store.select(getSideMenuStatus).pipe(take(1)).toPromise();
      let cssClass = '';
      cssClass += (this.isDesktopMode) ? 'update-password-modal-desktop' : 'update-password-modal';
      cssClass += (!sideMenuHidden && this.isDesktopMode) ? ' with-side-menu' : '';
      const modal = await this.modalCtrl.create({
        component: OnRamperComponent,
        cssClass
      });
      return await modal.present();
    }

    if (mode == 'buy') {
      // TODO: you can comment this to skip KYC for testing buy in prodcution mode
      var kycData = await this.complianceService.getKycCip();
      if (!kycData["passingLevel"]) {
        await openKycModal(sideMenuHidden, this.isDesktopMode, this.modalCtrl);
        return;
      }

    }

    let cssClass = "";
    cssClass += this.isDesktopMode ? "select-asset-modal-desktop" : "select-asset-modal";
    cssClass += !sideMenuHidden && this.isDesktopMode ? " with-side-menu" : "";
    const modal = await this.modalCtrl.create({
      component: SelectAssetModal,
      componentProps: {
        actionType: mode,
        // newMember: this.applyPromo == true ? true : false,
        // promocode: this.applyPromo == true ? this.newMemberOfferData.attributes.code : null
      },
      id: 'select-asset-model',
      cssClass,
      initialBreakpoint: this.isDesktopMode ? undefined : 0.6,
      breakpoints: this.isDesktopMode ? undefined : [0, 0.6, 0.9, 1],
    });
    await modal.present();
  }

  async dismissModal() {
    try {
      await this.modalCtrl.dismiss();
    } catch (error) { }
  }

}
